import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

export default function PayPalInvoiceLink({ invoiceId, balance }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const { user } = useAuth();
  const { data: normalLink, error } = useQuery({
    queryKey: ['paypal-invoice-link', invoiceId, balance],
    queryFn: async () => (await api.get(`/paypal/invoices/${invoiceId}/link`)).data,
    staleTime: 60000, retry: false,
  });
  const sandbox = normalLink?.environment === 'sandbox';
  const webhookOnly = sandbox && user?.ruolo === 'admin' && testMode;
  const test = useQuery({
    queryKey: ['paypal-invoice-webhook-test', invoiceId, balance],
    queryFn: async () => (await api.get(`/paypal/invoices/${invoiceId}/link?test=webhook`)).data,
    enabled: open && webhookOnly, staleTime: 0, retry: false,
  });
  if (error || !normalLink?.available) return null;
  const data = webhookOnly ? test.data : normalLink;
  const copy = async () => {
    try { await navigator.clipboard.writeText(data?.url); setCopied(true); }
    catch (_) { setCopied(false); } // The link remains selectable when clipboard permission is unavailable.
  };
  return <>
    <Button variant="outlined" onClick={() => { setOpen(true); setCopied(false); setTestMode(false); }}>Link PayPal{sandbox ? ' (test)' : ''}</Button>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Pagamento della fattura con PayPal</DialogTitle>
      <DialogContent>
        {sandbox && <Alert severity="warning" sx={{ mb: 2 }}>Ambiente Sandbox: i nuovi pagamenti di prova aggiornano realmente il saldo della fattura. Al termine annulla le associazioni in Pagamenti &gt; PayPal. Condividi il link solo per il collaudo.</Alert>}
        {sandbox && user?.ruolo === 'admin' && <FormControlLabel control={<Checkbox checked={webhookOnly}
          onChange={event => { setTestMode(event.target.checked); setCopied(false); }} />} label="Solo notifiche PayPal (collaudo)" />}
        {webhookOnly && <Alert severity="info" sx={{ mb: 2 }}>Questa prova si completa solo tramite le notifiche PayPal. Dopo l’approvazione puoi chiudere la pagina senza fretta e controllare l’esito nella fattura.</Alert>}
        {webhookOnly && test.isFetching && <Typography sx={{ mb: 2 }}>Preparazione del link di collaudo…</Typography>}
        {webhookOnly && (test.error || data?.available === false) && <Alert severity="error" sx={{ mb: 2 }}>{test.error?.response?.data?.message || data?.message || 'Collaudo non disponibile.'}</Alert>}
        <Typography sx={{ mb: 2 }}>Il cliente può aprire questo link senza accedere al gestionale. L’importo da pagare viene verificato al momento del pagamento.</Typography>
        {data?.qr_code && <Box sx={{ textAlign: 'center', mb: 2 }}><img src={data.qr_code} alt="QR per il pagamento della fattura" width="200" height="200" /></Box>}
        <TextField fullWidth label="Link di pagamento" value={data?.url || ''} multiline InputProps={{ readOnly: true }}
          onFocus={event => event.target.select()} />
        {copied && <Alert severity="success" sx={{ mt: 2 }}>Link copiato. Puoi incollarlo in un’email o in WhatsApp.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Chiudi</Button>
        <Button component="a" href={data?.available ? data.url : undefined} disabled={!data?.available} target="_blank" rel="noopener noreferrer">Apri pagina</Button>
        <Button variant="contained" onClick={copy} disabled={!data?.available}>Copia link</Button>
      </DialogActions>
    </Dialog>
  </>;
}
